import { Box, Button, FormControl, FormLabel, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { CommonDialog, CommonDialogHeader } from '..';
import { OrdersService } from '../../../api/services/ordersService';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IDispatchOrderRequest } from '../../../types/api/request/ordersrequest';
import { IDispatchOrderDialogProps, IDispatchOrderDialogBodyProps } from '../../../types/dialog';

const SLOT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

interface FormValues {
  reason: string;
  comment: string;
  packaging: string;
  slotStart: Dayjs | null;
  slotEnd: Dayjs | null;
}

export const DispatchOrderDialog = ({
  selectedOrder,
  isOpen,
  setIsOpen,
  onDispatch,
}: IDispatchOrderDialogProps) => {
  const dispatch = useDispatch();

  const onSubmit = async (payload: IDispatchOrderRequest) => {
    if (!selectedOrder) return;
    try {
      const result = await OrdersService.dispatchOrder(selectedOrder.order_number, payload);
      dispatch(
        showNotification({
          message: `Order rerun as ${result.newLocusOrderId} (attempt ${result.rerunSequence}).`,
        }),
      );
      setIsOpen(false);
      onDispatch();
    } catch (error: any) {
      const status = error?.response?.status;
      dispatch(
        showNotification({
          message:
            status === 403
              ? 'You do not have permission to rerun orders.'
              : (error?.response?.data?.message ?? 'Failed to rerun order.'),
          severity: 'error',
        }),
      );
    }
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      header={<CommonDialogHeader title="Rerun Order" onClose={() => setIsOpen(false)} />}
      body={
        selectedOrder ? (
          <DialogBody
            orderLabel={selectedOrder.ds_order_number ?? selectedOrder.order_number}
            onSubmit={onSubmit}
            onClose={() => setIsOpen(false)}
          />
        ) : null
      }
      footer={undefined}
    />
  );
};

const DialogBody = ({ orderLabel, onSubmit, onClose }: IDispatchOrderDialogBodyProps) => {
  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      reason: '',
      comment: '',
      packaging: '',
      slotStart: null,
      slotEnd: null,
    },
  });

  const handleFormSubmit = handleSubmit((data) => {
    const payload: IDispatchOrderRequest = {
      reason: data.reason || undefined,
      comment: data.comment || undefined,
      packaging: data.packaging || undefined,
      slotStart: data.slotStart ? data.slotStart.format(SLOT_FORMAT) : undefined,
      slotEnd: data.slotEnd ? data.slotEnd.format(SLOT_FORMAT) : undefined,
    };
    return onSubmit(payload);
  });

  return (
    <Box
      component="form"
      onSubmit={handleFormSubmit}
      noValidate
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
    >
      <Typography variant="body2" color="text.secondary">
        This cancels the current Locus delivery order for <strong>{orderLabel}</strong> and creates
        a new one under a suffixed id. Cin7 is not touched.
      </Typography>

      <FormControl>
        <FormLabel>Reason</FormLabel>
        <TextField
          placeholder="e.g. no driver accepted broadcast"
          fullWidth
          variant="outlined"
          error={!!errors.reason}
          helperText={errors.reason?.message}
          {...register('reason', { required: 'Reason is required' })}
        />
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box display="flex" gap={2}>
          <Controller
            name="slotStart"
            control={control}
            rules={{
              validate: (value) => {
                const { slotEnd } = getValues();
                if (!!value !== !!slotEnd) return 'Provide both slot start and end, or neither.';
                return true;
              },
            }}
            render={({ field }) => (
              <FormControl fullWidth>
                <FormLabel>Slot start (optional)</FormLabel>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.slotStart,
                      helperText: errors.slotStart?.message,
                    },
                  }}
                />
              </FormControl>
            )}
          />
          <Controller
            name="slotEnd"
            control={control}
            rules={{
              validate: (value) => {
                const { slotStart } = getValues();
                if (!!value !== !!slotStart) return 'Provide both slot start and end, or neither.';
                if (value && slotStart && !value.isAfter(slotStart)) {
                  return 'Slot end must be after slot start.';
                }
                return true;
              },
            }}
            render={({ field }) => (
              <FormControl fullWidth>
                <FormLabel>Slot end (optional)</FormLabel>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  minDateTime={getValues('slotStart') ?? undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.slotEnd,
                      helperText: errors.slotEnd?.message,
                    },
                  }}
                />
              </FormControl>
            )}
          />
        </Box>
      </LocalizationProvider>

      <FormControl>
        <FormLabel>Packaging (optional)</FormLabel>
        <TextField
          placeholder="e.g. large insulated bag"
          fullWidth
          variant="outlined"
          {...register('packaging')}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Delivery comment override (optional)</FormLabel>
        <TextField
          placeholder="Overrides the original order's comment for this delivery only"
          multiline
          rows={2}
          fullWidth
          variant="outlined"
          {...register('comment')}
        />
      </FormControl>

      <Box display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="warning" disabled={isSubmitting}>
          {isSubmitting ? 'Rerunning...' : 'Rerun Order'}
        </Button>
      </Box>
    </Box>
  );
};
